import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, Target, BarChart3, Map } from 'lucide-react';
import CampogramaViewer from './CampogramaViewer';
import { clubService } from '../../services/clubService';
import { campogramasService } from '../../services/campogramasService';
import type { TacticalTemplate, CampogramaData } from '../../types/tactical';

interface Team {
  id: number;
  name: string;
  formation: string;
  players_count: number;
}

const TacticalAnalysis: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'generator' | 'analysis' | 'templates'>('generator');
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [formations, setFormations] = useState<string[]>([]);
  const [selectedFormation, setSelectedFormation] = useState('4-3-3');
  const [templates, setTemplates] = useState<TacticalTemplate[]>([]);
  const [campogramaData, setCampogramaData] = useState<CampogramaData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load real data
  useEffect(() => {
    loadTeamsData();
    
    // Load formations
    setFormations(['4-3-3', '4-2-3-1', '4-4-2', '3-5-2', '5-3-2', '3-4-3']);

    // Load tactical templates
    setTemplates([
      {
        id: 'high_press_analysis',
        name: 'Análisis de Presión Alta',
        description: 'Analiza la intensidad y efectividad de la presión alta del equipo',
        metrics: ['pressing_intensity', 'recovery_positions', 'defensive_line_height'],
        visualization: 'heat_map_with_pressing_zones'
      },
      {
        id: 'possession_build_up',
        name: 'Construcción de Juego',
        description: 'Analiza los patrones de construcción desde atrás',
        metrics: ['pass_networks', 'build_up_zones', 'player_connections'],
        visualization: 'pass_network_diagram'
      },
      {
        id: 'attacking_patterns',
        name: 'Patrones de Ataque',
        description: 'Identifica las rutas y patrones de ataque más utilizados',
        metrics: ['attacking_zones', 'final_third_entries', 'shot_locations'],
        visualization: 'attacking_flow_diagram'
      }
    ]);
  }, []);

  const loadTeamsData = async () => {
    try {
      const teamsData = await clubService.getClubTeams();
      // Map the team data to match the expected Team interface
      const mappedTeams = teamsData.map(team => ({
        id: parseInt(team.id) || 0, // Convert string ID to number
        name: team.nombre,
        formation: '4-3-3', // Default formation, could be set from team data if available
        players_count: team.cantidad_jugadores || 0
      }));
      setTeams(mappedTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
      // On error, set empty teams array instead of mock data
      setTeams([]);
    }
  };

  const generateCampograma = async () => {
    if (!selectedTeam) return;

    setIsLoading(true);
    try {
      // Try to get real team positioning data from the API
      const teamPositioning = await campogramasService.getTeamPositioning(selectedTeam.id);
      setCampogramaData(teamPositioning.campograma);
    } catch (error) {
      console.error('Error generating campograma:', error);
      // If the API fails, we can't provide a meaningful campograma
      // Set null instead of mock data
      setCampogramaData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Análisis Táctico</h2>
            <p className="text-gray-600 mt-1">
              Genera y analiza campogramas tácticos interactivos
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Map className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('generator')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'generator'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Target size={16} />
                <span>Generador de Campogramas</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analysis'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 size={16} />
                <span>Análisis Avanzado</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Eye size={16} />
                <span>Plantillas Tácticas</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Generator Tab */}
          {activeTab === 'generator' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Team Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Equipo
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar equipo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    {filteredTeams.map(team => (
                      <button
                        key={team.id}
                        onClick={() => setSelectedTeam(team)}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                          selectedTeam?.id === team.id ? 'bg-blue-50 text-blue-600' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{team.name}</span>
                          <span className="text-sm text-gray-500">{team.formation}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Formation Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Formación
                  </label>
                  <select
                    value={selectedFormation}
                    onChange={(e) => setSelectedFormation(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {formations.map(formation => (
                      <option key={formation} value={formation}>
                        {formation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Generate Button */}
                <div className="flex items-end">
                  <button
                    onClick={generateCampograma}
                    disabled={!selectedTeam || isLoading}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Generando...' : 'Generar Campograma'}
                  </button>
                </div>
              </div>

              {/* Campograma Viewer */}
              <CampogramaViewer
                data={campogramaData as any}
                isLoading={isLoading}
                showHeatMap={true}
                showInfluenceZones={true}
                interactive={true}
                onPlayerClick={(player) => {
                  console.log('Jugador seleccionado:', player);
                }}
                onPlayersMove={(updatedPlayers) => {
                  if (campogramaData) {
                    setCampogramaData({
                      ...campogramaData,
                      players: updatedPlayers as any
                    });
                  }
                }}
                onFormationSave={(formationData) => {
                  console.log('Guardando formación personalizada:', formationData);
                  // Aquí se podría enviar al backend para guardar la formación
                  alert(`Formación "${formationData.name}" guardada exitosamente`);
                }}
              />
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === 'analysis' && (
            <div className="space-y-6">
              <div className="text-center py-12">
                <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Análisis Táctico Avanzado
                </h3>
                <p className="text-blue-100 mb-6">
                  Funcionalidad en desarrollo. Próximamente disponible análisis comparativo entre equipos,
                  métricas tácticas avanzadas y reportes detallados.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Comparación de Equipos</h4>
                    <p className="text-sm text-blue-100">
                      Compara métricas tácticas entre diferentes equipos y formaciones.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Análisis Temporal</h4>
                    <p className="text-sm text-blue-100">
                      Visualiza la evolución táctica a lo largo del tiempo.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Reportes Automáticos</h4>
                    <p className="text-sm text-blue-100">
                      Genera reportes tácticos automáticos con insights y recomendaciones.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Plantillas de Análisis Táctico
                </h3>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  <Download size={16} className="inline mr-2" />
                  Exportar Plantillas
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map(template => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                      <Eye className="h-5 w-5 text-gray-400" />
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      {template.description}
                    </p>
                    
                    <div className="mb-4">
                      <h5 className="text-xs font-medium text-gray-700 mb-2">Métricas incluidas:</h5>
                      <div className="flex flex-wrap gap-1">
                        {template.metrics.map(metric => (
                          <span
                            key={metric}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {metric}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <button className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                      Aplicar Plantilla
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TacticalAnalysis;
